/** Start screen (role, key level, bot preset) and end screen (chest + meters). */
import type { AffixId, BotSkillPreset, ContentRegistry, RunResult } from '@keystone/sim';
import { BOT_SKILL_PRESETS } from '@keystone/sim';
import { METER_MODES, type MeterMode, type MeterRow } from '../game/meters';
import { ROLE_COLOR } from '../render/palette';
import { RENDERER_KINDS, type RendererKind } from '../render/settings';
import { el, onPress } from './dom';
import { chestLabel, clock, shortNumber } from './format';
import { metaBridge } from '../meta/metaBridge';
import './screens.css';

export interface StartConfig {
  /** Party slot the human plays; -1 = spectate (bots play all four). */
  slot: number;
  level: number;
  preset: BotSkillPreset;
  seed: number;
  dungeonId: string;
  /** 'auto' = derived from the key level (affixesForLevel). */
  affixes: AffixId[] | 'auto';
  /** Renderer for the run (also in the in-game graphics settings). */
  renderer?: RendererKind;
}

/** Bot presets offered in the UI (the contract's names; competent/expert are the tuned ones). */
export const BOT_PRESET_NAMES = Object.keys(BOT_SKILL_PRESETS) as BotSkillPreset[];
export const DEFAULT_BOT_PRESET: BotSkillPreset = 'competent';

const AFFIX_OPTIONS: { label: string; value: AffixId[] | 'auto' }[] = [
  { label: 'By key level', value: 'auto' },
  { label: 'Fortified', value: ['fortified'] },
  { label: 'Tyrannical', value: ['tyrannical'] },
  { label: 'None', value: [] },
];

export function showStartScreen(parent: HTMLElement, content: ContentRegistry, party: readonly string[], defaults: StartConfig, onStart: (c: StartConfig) => void): () => void {
  const root = el('div', 'screen start hk-screen', parent);
  const box = el('div', 'screen-box', root);
  el('h1', '', box, 'Keystone Sim');
  el('div', 'subtitle', box, 'Pick your role. Bots play the rest.');

  let slot = defaults.slot;
  const roles = el('div', 'role-cards', box);
  const cards: HTMLElement[] = [];
  const choose = (i: number): void => {
    slot = i;
    cards.forEach((c, j) => c.classList.toggle('active', j === (i < 0 ? party.length : i)));
  };
  party.forEach((classId, i) => {
    const cls = content.class(classId);
    const card = el('button', 'role-card', roles);
    card.type = 'button';
    const role = cls?.role ?? 'dps';
    card.style.setProperty('--role', ROLE_COLOR[role]);
    el('div', 'role-name', card, cls?.name ?? classId);
    el('div', 'role-role', card, i === 3 && role === 'dps' ? 'Ranged DPS' : i === 2 && role === 'dps' ? 'Melee DPS' : role[0]!.toUpperCase() + role.slice(1));
    onPress(card, () => choose(i));
    cards.push(card);
  });
  const spec = el('button', 'role-card spectate', roles);
  spec.type = 'button';
  el('div', 'role-name', spec, 'Spectate');
  el('div', 'role-role', spec, 'Bots play all four');
  onPress(spec, () => choose(-1));
  cards.push(spec);
  choose(slot);

  const form = el('div', 'start-form', box);
  const field = (label: string): HTMLElement => {
    const f = el('label', 'field', form);
    el('span', '', f, label);
    return f;
  };
  const level = el('input', '', field('Key level'));
  level.type = 'number';
  level.min = '2';
  level.max = '15';
  level.value = String(defaults.level);

  const preset = el('select', '', field('Bot skill'));
  for (const p of BOT_PRESET_NAMES) el('option', '', preset, p[0]!.toUpperCase() + p.slice(1)).value = p;
  preset.value = defaults.preset;

  const affix = el('select', '', field('Affix'));
  AFFIX_OPTIONS.forEach((o, i) => (el('option', '', affix, o.label).value = String(i)));
  affix.value = String(Math.max(0, AFFIX_OPTIONS.findIndex((o) => String(o.value) === String(defaults.affixes))));

  const dungeon = el('select', '', field('Dungeon'));
  for (const d of content.bundle.dungeons.filter((x) => !x.field && !x.mobLevel)) el('option', '', dungeon, d.name).value = d.id;
  dungeon.value = content.dungeon(defaults.dungeonId) ? defaults.dungeonId : (content.bundle.dungeons[0]?.id ?? '');

  const seed = el('input', '', field('Seed'));
  seed.type = 'number';
  seed.value = String(defaults.seed);

  const gfx = el('select', '', field('Graphics'));
  const GFX_LABEL: Record<RendererKind, string> = { toon: 'Toon (art style)', placeholder: 'Placeholder (debug)' };
  for (const k of RENDERER_KINDS) el('option', '', gfx, GFX_LABEL[k]).value = k;
  gfx.value = defaults.renderer ?? 'toon';

  const go = el('button', 'btn primary', box, 'Start Key');
  go.type = 'button';
  onPress(go, () => {
    const lv = Math.min(15, Math.max(2, Math.round(Number(level.value) || 2)));
    onStart({
      slot,
      level: lv,
      preset: preset.value as BotSkillPreset,
      seed: Math.round(Number(seed.value) || 1),
      dungeonId: dungeon.value,
      affixes: AFFIX_OPTIONS[Number(affix.value)]?.value ?? 'auto',
      renderer: gfx.value as RendererKind,
    });
  });

  el('div', 'controls-help', box).innerHTML = [
    '<b>W/S</b> run / backpedal &middot; <b>A/D</b> strafe &middot; <b>Right-drag</b> turn &middot; <b>Left-drag</b> look &middot; <b>Wheel</b> zoom',
    '<b>1-4 Q E R F</b> abilities &middot; <b>G</b> / <b>Shift+F</b> extra (Grip / Charge / Freedom) &middot; <b>Tab</b> next enemy &middot; <b>Click</b> target &middot; <b>F1-F4</b> party',
    '<b>Esc</b> cancel cast / clear target &middot; <b>P</b> pause &middot; Mouse-over party frames to heal them',
  ].join('<br>');

  return () => root.remove();
}

export interface EndScreenData {
  result: RunResult;
  dungeonName: string;
  rows: Record<MeterMode, MeterRow[]>;
}

const MODE_LABEL: Record<MeterMode, string> = { damage: 'Damage Done', healing: 'Healing Done', taken: 'Damage Taken', interrupts: 'Interrupts' };

export interface EndScreenExtras {
  /** Rendered under the run stats (progression rewards); returns a cleanup. */
  rewards?: (parent: HTMLElement) => (() => void) | void;
  /** Label of the menu button (default "Main menu"). */
  menuLabel?: string;
  /** Hide "Run it again". */
  hideAgain?: boolean;
}

export function showEndScreen(parent: HTMLElement, data: EndScreenData, actions: { again(): void; menu(): void }, extras: EndScreenExtras = {}): () => void {
  const r = data.result;
  // A meta overlay opened during the key must not sit over the results.
  metaBridge.closeOverlay();
  const root = el('div', 'screen end hk-screen', parent);
  const box = el('div', 'screen-box wide', root);
  const chest = el('div', `chest ${r.timed ? 'timed' : 'depleted'}`, box, chestLabel(r.chest, r.completed));
  chest.title = r.timed ? 'Timed' : r.completed ? 'Over time' : 'Not completed';
  el('h2', '', box, `${data.dungeonName} +${r.level}${r.affixes.length ? ` (${r.affixes.join(', ')})` : ''}`);
  const stats = el('div', 'end-stats', box);
  const stat = (label: string, value: string): void => {
    const s = el('div', 'end-stat', stats);
    el('div', 'end-stat-value', s, value);
    el('div', 'end-stat-label', s, label);
  };
  stat('Time', `${clock(r.elapsedMs)} / ${clock(r.timerMs)}`);
  stat('Deaths', `${r.deaths}`);
  stat('Lost to deaths', clock(r.deathPenaltyMs));
  stat('Out of combat', clock(r.outOfCombatMs));
  stat('Forces', `${r.forces}/${r.forcesRequired}`);
  stat('Pulls / wipes', `${r.pulls} / ${r.wipes}`);
  let cleanupRewards: (() => void) | void;
  if (extras.rewards) {
    try {
      cleanupRewards = extras.rewards(box);
    } catch (err) {
      console.error('[web] rewards view failed', err);
    }
  }

  const grid = el('div', 'end-meters', box);
  for (const mode of METER_MODES) {
    const col = el('div', 'panel meters static', grid);
    el('div', 'meter-title', col, MODE_LABEL[mode]);
    const rows = data.rows[mode];
    const top = Math.max(1, rows[0]?.value ?? 1);
    for (const [i, row] of rows.entries()) {
      const line = el('div', 'meter-row', col);
      const fill = el('div', 'meter-fill', line);
      fill.style.width = `${(row.value / top) * 100}%`;
      fill.style.background = row.color;
      el('div', 'meter-name', line, `${i + 1}. ${row.name}`);
      el('div', 'meter-value', line, mode === 'interrupts' ? `${row.value}` : `${shortNumber(row.value)} (${shortNumber(row.perSec)})`);
    }
  }
  const btns = el('div', 'end-buttons', box);
  const menu = el('button', `btn${extras.menuLabel ? ' primary' : ''}`, btns, extras.menuLabel ?? 'Main menu');
  onPress(menu, actions.menu);
  if (!extras.hideAgain) {
    const again = el('button', `btn${extras.menuLabel ? '' : ' primary'}`, btns, 'Run it again');
    onPress(again, actions.again);
    if (!extras.menuLabel) btns.prepend(again);
  }
  return () => {
    if (typeof cleanupRewards === 'function') cleanupRewards();
    root.remove();
  };
}
